import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { PiArrowRightBold, PiTrashBold } from 'react-icons/pi';
import ShipmentClientsCell from './ShipmentClientsCell';
import ShipmentDealsCell from './ShipmentDealsCell';
import ShipmentInvoiceCell from './ShipmentInvoiceCell';
import SupplierBrandCell from './SupplierBrandCell';
import type { ShipmentListRow } from './types';
import { formatDateTime, riskColor } from './utils';
import api from '@v2/lib/api';
import { hasAnyRole, COMEX_ROLES } from '@v2/lib/auth';
import ConfirmModal from '@v2/components/ui/ConfirmModal';

function hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3
        ? clean.split('').map((c) => c + c).join('')
        : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

type StageBadgeColor = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'DEFAULT';

function stageColor(color: string): StageBadgeColor {
    const map: Record<string, StageBadgeColor> = {
        gray: 'DEFAULT',
        blue: 'info',
        indigo: 'info',
        green: 'success',
        yellow: 'warning',
        orange: 'warning',
        red: 'danger',
        purple: 'primary',
        primary: 'primary',
    };
    return map[color] ?? 'DEFAULT';
}

const columnHelper = createColumnHelper<ShipmentListRow>();

type Props = {
    rows: ShipmentListRow[];
    loading: boolean;
    page: number;
    perPage: number;
    lastPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    onDeleted?: (id: number) => void;
};

export default function ShipmentListTable({
    rows,
    loading,
    page,
    perPage,
    lastPage,
    total,
    onPageChange,
    onPerPageChange,
    onDeleted,
}: Props) {
    const navigate = useNavigate();
    const canEditInvoice = hasAnyRole([...COMEX_ROLES]);
    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const confirmRow = rows.find((r) => r.id === confirmId) ?? null;

    async function handleDelete(id: number) {
        setDeletingId(id);
        try {
            await api.delete(`/shipments/${id}`);
            onDeleted?.(id);
        } finally {
            setDeletingId(null);
            setConfirmId(null);
        }
    }
    const columns = useMemo(
        () => [
            columnHelper.accessor('order_number', {
                header: 'Embarque',
                cell: ({ row }) => (
                    <div className="flex flex-col gap-0.5">
                        <Link
                            to={`/shipments/${row.original.id}`}
                            className="font-semibold text-gray-900 hover:text-amber-600 hover:underline dark:text-zinc-100 dark:hover:text-amber-400"
                        >
                            {row.original.process_code || row.original.order_number || `#${row.original.id}`}
                        </Link>
                        {row.original.process_code && row.original.order_number && (
                            <span className="text-xs text-gray-500 dark:text-zinc-500">
                                Pedido {row.original.order_number}
                            </span>
                        )}
                        {row.original.process_name && (
                            <span className="text-xs text-gray-500 truncate max-w-[160px] dark:text-zinc-500" title={row.original.process_name}>
                                {row.original.process_name}
                            </span>
                        )}
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'linked_deals',
                header: 'Pedidos no embarque',
                cell: ({ row }) => (
                    <ShipmentDealsCell
                        deals={row.original.linked_deals ?? []}
                        count={row.original.linked_deals_count ?? 0}
                    />
                ),
            }),
            columnHelper.display({
                id: 'invoice_number',
                header: 'Nº Invoice',
                cell: ({ row }) => (
                    <ShipmentInvoiceCell
                        shipmentId={row.original.id}
                        value={row.original.invoice}
                        canEdit={canEditInvoice}
                    />
                ),
            }),
            columnHelper.display({
                id: 'clients',
                header: 'Clientes',
                cell: ({ row }) => (
                    <ShipmentClientsCell clients={row.original.clients_summary ?? []} />
                ),
            }),
            columnHelper.display({
                id: 'supplier',
                header: 'Fornecedor',
                cell: ({ row }) => (
                    <SupplierBrandCell
                        display={row.original.supplier_display}
                        fallbackLabel={row.original.supplier_label}
                    />
                ),
            }),
            columnHelper.accessor('order_date', {
                header: 'Data',
                cell: ({ getValue }) => {
                    const { date, time } = formatDateTime(getValue());
                    return (
                        <div className="whitespace-nowrap">
                            <div className="text-gray-900">{date}</div>
                            {time && <div className="text-xs text-gray-500">{time}</div>}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'stage',
                header: 'Estágio / Tarefa',
                cell: ({ row }) => {
                    const { stage_label, stage_color, current_task } = row.original;
                    return (
                        <div className="min-w-[180px] space-y-1.5">
                            <Badge
                                variant="flat"
                                color={stageColor(stage_color ?? 'gray')}
                                className="text-[11px] font-semibold"
                            >
                                {stage_label}
                            </Badge>
                            {current_task && (
                                <div className="flex items-center gap-1">
                                    <PiArrowRightBold className="h-2.5 w-2.5 shrink-0 text-gray-400" />
                                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium leading-tight text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                        {current_task.sort_order}. {current_task.title}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: 'financeiro',
                header: 'Financeiro',
                cell: ({ row }) => (
                    <span className="text-gray-700">
                        {row.original.invoice_financial_date
                            ? 'Pago'
                            : row.original.invoice
                              ? 'Invoice'
                              : 'Pendente'}
                    </span>
                ),
            }),
            columnHelper.accessor('process_status', {
                header: 'Status',
                cell: ({ row }) => (
                    <div className="flex items-center gap-1.5">
                        <Badge renderAsDot color={row.original.process_status === 'Finalizado' ? 'success' : 'info'} />
                        <span className="text-gray-800">{row.original.process_status}</span>
                        {row.original.delay_risk_level && (
                            <Badge variant="flat" className="ml-1 gap-1">
                                <span className={`h-1.5 w-1.5 rounded-full ${riskColor(row.original.delay_risk_level)}`} />
                                {row.original.delay_risk_level}
                            </Badge>
                        )}
                    </div>
                ),
            }),
            columnHelper.accessor('shipping_line', {
                header: 'Armador',
                cell: ({ getValue }) => <span className="text-gray-700">{getValue() || '—'}</span>,
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) => {
                    const id = row.original.id;
                    return (
                        <div className="flex items-center gap-2">
                            <Link
                                to={`/shipments/${id}`}
                                className="text-sm font-medium text-amber-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                Abrir
                            </Link>
                            <button
                                onClick={(e) => { e.stopPropagation(); setConfirmId(id); }}
                                className="text-gray-300 hover:text-red-500 transition-colors"
                                title="Apagar embarque"
                            >
                                <PiTrashBold className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    );
                },
            }),
        ],
        [canEditInvoice],
    );

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
        <ConfirmModal
            open={confirmId !== null}
            onClose={() => setConfirmId(null)}
            onConfirm={() => confirmId !== null && handleDelete(confirmId)}
            title="Apagar embarque"
            description={confirmRow ? `Tem certeza que deseja apagar o embarque "${confirmRow.process_code ?? confirmRow.process_name ?? confirmRow.order_number}"? Esta ação não pode ser desfeita.` : 'Tem certeza que deseja apagar este embarque?'}
            confirmLabel="Sim, apagar"
            cancelLabel="Cancelar"
            loading={deletingId !== null}
        />
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <div className="ds-table-container border-0">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="border-b border-gray-200 bg-gray-50/80">
                            {table.getHeaderGroups().map((hg) => (
                                <tr key={hg.id}>
                                    {hg.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                                        Carregando embarques…
                                    </td>
                                </tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                                        Nenhum embarque encontrado.
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                table.getRowModel().rows.map((row) => {
                                    const rowColor = row.original.stage_row_color;
                                    return (
                                        <tr
                                            key={row.id}
                                            className="cursor-pointer transition-colors hover:brightness-95"
                                            style={rowColor ? {
                                                backgroundColor: hexToRgba(rowColor, 0.10),
                                                borderLeft: `3px solid ${rowColor}`,
                                            } : { borderLeft: '3px solid transparent' }}
                                            onClick={() => navigate(`/shipments/${row.original.id}`)}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id} className="px-4 py-3 align-top">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>

            <TablePagination
                meta={{
                    current_page: page,
                    last_page: lastPage,
                    per_page: perPage,
                    total,
                }}
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
                perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                itemLabel="embarque"
            />
        </div>
        </>
    );
}
